import has from "lodash/has";
import kebabCase from "lodash/kebabCase";
import {
  VECTOR_IMAGE_ICON,
  VECTOR_IMAGE_BRAND,
  VECTOR_IMAGE_PAYMENT,
} from "@eventbrite/valkyrie-eds-vector-image";
import { IconProps } from "./Icon";
import React from "react";

type VectorType = keyof typeof VECTOR_TYPE_TESTS;
type VectorTypeTest =
  (typeof VECTOR_TYPE_TESTS)[keyof typeof VECTOR_TYPE_TESTS];
type IconVectorType =
  | typeof VECTOR_IMAGE_ICON
  | typeof VECTOR_IMAGE_BRAND
  | typeof VECTOR_IMAGE_PAYMENT;

const VECTOR_TYPE_TESTS = {
  [VECTOR_IMAGE_BRAND]: (name: string) =>
    name.includes(`-${VECTOR_IMAGE_BRAND}`),
  [VECTOR_IMAGE_PAYMENT]: (name: string) =>
    name.includes(`-${VECTOR_IMAGE_PAYMENT}`),
} as const;
const SVG_COMPONENT_NAME_MATCH = /^(.+)-svg$/;

/**
 * Formats name by kebab-casing and then stripping off starting "svg-"
 * @param {string} svgComponentName PascalCase SVG component name
 * @returns {string} formatted named
 */
const formatSvgComponentName = (svgComponentName?: string) => {
  const kebabCaseName = kebabCase(svgComponentName);
  const match = kebabCaseName.match(SVG_COMPONENT_NAME_MATCH);

  return match ? match[1] : kebabCaseName;
};

/**
 * Whether or not the specified matchers match the specified icon type
 * @param {import('react').ReactElement|string} iconType Icon type
 * @param {(svgComponentName: string) => boolean} svgNameMatcher Matcher when iconType is an SVG Component
 * @returns {boolean}
 */
export const iconNameMatches = (
  iconType: IconProps["type"],
  svgNameMatcher: VectorTypeTest,
) => {
  const nodeType = (iconType as unknown as React.ReactElement).type;
  if (
    typeof nodeType === "function" &&
    typeof (nodeType as React.FunctionComponent).displayName === "string"
  ) {
    const name = (nodeType as React.FunctionComponent).displayName || "";
    return svgNameMatcher(formatSvgComponentName(name) as string);
  }
};

/**
 * Gets the type of vector image based on the specified icon type
 * (NOTE: This is only intended for icons, not graphics or illustrations)
 * @param {import('react').ReactElement} iconType Icon type
 * @returns {'icon'|'brand'|'payment'}
 */
export const getIconVectorType = (iconType: IconProps["type"]) => {
  const foundVectorType = Object.keys(VECTOR_TYPE_TESTS).find((vectorType) => {
    const svgTest = VECTOR_TYPE_TESTS[vectorType as VectorType];

    return iconNameMatches(iconType, svgTest);
  });

  return (foundVectorType || VECTOR_IMAGE_ICON) as IconVectorType;
};
